import { ComponentType } from 'react';
import { SvgIcon, SvgIconProps } from '@material-ui/core';
import {
  TbBuildingStore,
  TbServer2,
  TbBook2,
  TbFileText,
  TbUsers,
  TbPuzzle,
  TbSettings,
} from 'react-icons/tb';
import BuildIcon from '@material-ui/icons/Build';
import LockIcon from '@material-ui/icons/Lock';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarSpace,
} from '@backstage/core-components';
import { SidebarLogo } from './SidebarLogo';
import { SearchModalProvider, useSearchModal } from '@backstage/plugin-search';
import { Settings as SidebarSettings } from '@backstage/plugin-user-settings';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import {
  useRolloutFlag,
  PERMISSIONS_TAB_FLAG,
  GROUP_MANAGEMENT_FLAG,
} from '../../featureFlags';
import { useCanViewPlugin } from '@internal/plugin-rebac-explorer-frontend';
import type {
  NavContentComponent,
  NavContentNavItem,
} from '@backstage/plugin-app-react';
import { SpikeSearchModal } from '../search/SpikeSearchModal';

// SidebarItem.icon expects SvgIconComponent; react-icons não implementa essa interface.
function tablerIcon(TablerIcon: ComponentType<{ size?: number }>) {
  return (props: SvgIconProps) => (
    <SvgIcon {...props}>
      <TablerIcon size={24} />
    </SvgIcon>
  );
}

const MarketplaceIcon = tablerIcon(TbBuildingStore);
const ResourcesIcon = tablerIcon(TbServer2);
const GetStartedIcon = tablerIcon(TbBook2);
const DocsIcon = tablerIcon(TbFileText);
const GroupsIcon = tablerIcon(TbUsers);
const ApisIcon = tablerIcon(TbPuzzle);
const SettingsIcon = tablerIcon(TbSettings);

// Itens auto-descobertos que têm equivalente manual abaixo ou que não devem aparecer.
const NAV_ITEMS_RENDERED_MANUALLY = [
  'page:search',
  'page:home',
  'page:ciandtspike-marketplace/marketplace',
  'page:ciandtspike-marketplace/provisioned-resources',
  'page:ciandtspike-marketplace/tasks',
  'page:catalog',
  'page:techdocs',
  'page:api-docs',
  'page:user-settings',
  'page:devtools',
  'page:scaffolder',
  'page:catalog-import',
  'page:catalog-graph',
  'page:infrawallet',
  'page:grafana-observability-frontend/observability',
  // FLWP-69578 fix: these were never added when S1.1/S2.2 registered the
  // group-management pages, so the auto-discovered nav item (this page's own
  // `title: 'Groups'`) rendered alongside `GroupsNavItem` below — a literal
  // duplicate "Groups" entry, only visible once the S1.1/S2.1 bump PR
  // actually merged into this host (the page didn't exist here before that).
  'page:ciandtspike-group-management/group-management',
  'page:ciandtspike-group-management/group-management-detail',
  // FLWP-69584 fix: "Create Group" got its own auto-discovered sidebar entry
  // once S3.1 registered the page — creation is meant to happen only via the
  // "Criar Grupo" button inside the Groups list page, never as a standalone
  // menu item.
  'page:ciandtspike-group-management/group-management-create',
];

const AccessManagementItem = () => {
  const flagEnabled = useRolloutFlag(PERMISSIONS_TAB_FLAG);
  const canView = useCanViewPlugin('access-management');
  if (!flagEnabled || !canView) return null;
  return <SidebarItem icon={LockIcon} to="access" text="Gestão de Acesso" />;
};

// RN-09 da S2.1 (group-management) — substitui o destino do item "Groups"
// existente, nunca duplica: com o toggle ligado, aponta para a nova tela do
// plugin; desligado, mantém o comportamento atual (catálogo genérico
// filtrado). Mesmo padrão do AccessManagementItem acima.
const GroupsNavItem = () => {
  const flagEnabled = useRolloutFlag(GROUP_MANAGEMENT_FLAG);
  return (
    <SidebarItem
      icon={GroupsIcon}
      to={flagEnabled ? 'group-management' : 'catalog?filters[kind]=group'}
      text="Groups"
    />
  );
};

const DiscoveredNavItem = ({ href, title, icon }: NavContentNavItem) => (
  <SidebarItem icon={() => icon} to={href} text={title} />
);

const SearchButton = () => {
  const { toggleModal } = useSearchModal();
  return <SidebarItem icon={SearchIcon} text="Search" onClick={toggleModal} />;
};

export const SidebarNav: NavContentComponent = ({ navItems }) => {
  NAV_ITEMS_RENDERED_MANUALLY.forEach(id => navItems.take(id));
  const renderedNavItems = navItems.withComponent(DiscoveredNavItem);

  return (
    <SearchModalProvider>
      <SpikeSearchModal />
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SearchButton />
        </SidebarGroup>
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          <SidebarItem
            icon={MarketplaceIcon}
            to="marketplace"
            text="Marketplace"
          />
          <SidebarItem
            icon={ResourcesIcon}
            to="provisioned-resources"
            text="Resources"
          />
          <SidebarItem
            icon={GetStartedIcon}
            to="/onboarding"
            text="Get Started"
          />
          <SidebarDivider />
          <SidebarItem icon={DocsIcon} to="docs" text="Docs" />
          <GroupsNavItem />
          <SidebarItem icon={ApisIcon} to="api-docs" text="APIs" />
          <AccessManagementItem />
          {/* Plugins /alpha futuros são auto-descobertos e renderizados aqui. */}
          {renderedNavItems.rest()}
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <SidebarGroup label="Settings" icon={<SettingsIcon />}>
          <SidebarSettings />
          <RequirePermission
            permission={devToolsAdministerPermission}
            errorPage={<></>}
          >
            <SidebarItem icon={BuildIcon} to="devtools" text="DevTools" />
          </RequirePermission>
        </SidebarGroup>
      </Sidebar>
    </SearchModalProvider>
  );
};
